import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Inbox, Sparkles, AlertCircle, Activity, Server, ChevronUp, X } from 'lucide-react';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useEmailStats } from '@/hooks/useEmails';
import { useSystemStatus } from '@/hooks/useSystem';
import useAppStore from '@/store/appStore';
import { formatRelativeTime } from '@/utils/formatters';

export default function StatusBar() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useEmailStats();
  const { data: systemStatus } = useSystemStatus();
  const { agentStatus, activeTasks, removeTask } = useAppStore();
  const [showTasks, setShowTasks] = useState(false);
  const [backendStatus, setBackendStatus] = useState({ running: false });

  const totalEmails = stats?.total || 0;
  const unreadEmails = stats?.unread || 0;
  const newEmails = stats?.new_last_5min || 0;
  const actionNeeded = stats?.action_needed || 0;

  const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron;

  // Poll backend status from Electron
  useEffect(() => {
    if (!isElectron) return;

    const checkBackendStatus = async () => {
      try {
        const status = await window.electronAPI.getBackendStatus();
        setBackendStatus(status);
      } catch (err) {
        console.error('Failed to get backend status:', err);
      }
    };

    // Check immediately
    checkBackendStatus();

    // Then poll every 5 seconds
    const interval = setInterval(checkBackendStatus, 5000);

    return () => clearInterval(interval);
  }, [isElectron]);

  const handleUnreadClick = () => {
    navigate('/emails?seen=false');
  };

  const handleNewClick = () => {
    // Navigate to emails from last 5 minutes (recent first)
    navigate('/emails?order_by=date&order_dir=DESC');
  };

  const handleActionNeededClick = () => {
    navigate('/emails?action_needed=true');
  };

  return (
    <>
      {/* Task Panel (slides up from bottom) */}
      {showTasks && activeTasks.length > 0 && (
        <div className="border-t bg-card">
          <div className="px-4 py-2 max-h-48 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Active Tasks</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowTasks(false)}
                className="p-1 h-auto"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {activeTasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between gap-3 text-sm bg-muted/50 rounded px-3 py-2"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <LoadingSpinner size="sm" />
                    <span className="truncate">{task.title}</span>
                    {task.progress && (
                      <span className="text-xs text-muted-foreground">
                        {task.progress}
                      </span>
                    )}
                  </div>
                  {task.startedAt && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatRelativeTime(task.startedAt)}
                    </span>
                  )}
                  {task.cancellable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTask(task.id)}
                      className="p-1 h-auto"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Status Bar */}
      <div className="flex items-center justify-between gap-4 border-t bg-card px-4 py-2 text-xs">
        {/* Left: Email Stats */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{statsLoading ? '...' : totalEmails.toLocaleString()}</span>
            <span className="text-muted-foreground">total</span>
          </div>

          <button
            onClick={handleUnreadClick}
            className="flex items-center gap-1.5 hover:bg-muted/50 px-2 py-1 rounded transition-colors cursor-pointer"
            disabled={unreadEmails === 0}
          >
            <Inbox className="h-3.5 w-3.5 text-blue-500" />
            <span className="font-medium text-blue-500">{statsLoading ? '...' : unreadEmails.toLocaleString()}</span>
            <span className="text-muted-foreground">unread</span>
          </button>

          {newEmails > 0 && (
            <button
              onClick={handleNewClick}
              className="flex items-center gap-1.5 hover:bg-muted/50 px-2 py-1 rounded transition-colors cursor-pointer"
            >
              <Sparkles className="h-3.5 w-3.5 text-green-500" />
              <span className="font-medium text-green-500">{newEmails.toLocaleString()}</span>
              <span className="text-muted-foreground">new</span>
            </button>
          )}

          {actionNeeded > 0 && (
            <button
              onClick={handleActionNeededClick}
              className="flex items-center gap-1.5 hover:bg-muted/50 px-2 py-1 rounded transition-colors cursor-pointer"
            >
              <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
              <span className="font-medium text-orange-500">{actionNeeded.toLocaleString()}</span>
              <span className="text-muted-foreground">action needed</span>
            </button>
          )}
        </div>

        {/* Center: Active Tasks Button */}
        {activeTasks.length > 0 && (
          <button
            onClick={() => setShowTasks(!showTasks)}
            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/50 transition-colors"
          >
            <LoadingSpinner size="sm" />
            <span className="font-medium">{activeTasks.length} active {activeTasks.length === 1 ? 'task' : 'tasks'}</span>
            <ChevronUp className={cn('h-3 w-3 transition-transform', showTasks ? '' : 'rotate-180')} />
          </button>
        )}

        {/* Right: Agent & Backend Status */}
        <div className="flex items-center gap-4">
          {/* Agent Status */}
          <div className="flex items-center gap-2">
            <div
              className={cn(
                'h-2 w-2 rounded-full',
                agentStatus.isRunning ? 'bg-green-500' : 'bg-gray-400'
              )}
            />
            <Activity className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">
              Agent: {agentStatus.isRunning ? 'Running' : 'Stopped'}
            </span>
            {agentStatus.uptime > 0 && (
              <span className="text-muted-foreground">
                ({Math.floor(agentStatus.uptime / 60)}m)
              </span>
            )}
          </div>

          {/* Backend Status (Electron only) */}
          {isElectron && (
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  backendStatus?.running ? 'bg-green-500' : 'bg-gray-400'
                )}
              />
              <Server className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">
                Backend: {backendStatus?.running ? 'Running' : 'Stopped'}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function cn(...inputs) {
  return inputs.filter(Boolean).join(' ');
}
